import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import Page from '../../app/layout/Page';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import { MenuItem } from '../../components/Menu';
import Timestamp from '../../components/Timestamp';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import AttributeHandler from '../../editor/AttributeHandler';
import { EditorContainer } from '../../editor/Editor';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import Gutter from '../../editor/Gutter';
import QuillContext, { useQuill } from '../../editor/QuillContext';
import useAnchorScrollingHandler from '../../editor/useAnchorScrollingHandler';
import useHeaderCollapse from '../../editor/useHeaderCollapse';
import useListCollapse from '../../editor/useListCollapse';
import useSyncMagneticWallStates from '../../editor/useSyncMagneticWallState';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import InternalTopbar from '../../topbar/InternalTopbar';
import { UIActions } from '../../ui/uiSlice';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import PageMeta from '../../utils/PageMeta';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import postPath from '../../utils/url/postPath';
import ControlBarContributorsButton from '../ControlBarContributorsButton';
import PostLayout from '../PostLayout';
import PostLayoutProvider from '../PostLayoutProvider';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import { NewPostHistoryViewTOC } from '../PostStickyControl';
import { hasThreadsOrAnnotationsInOps } from '../useHasMagneticWallItems';
import type { VersionChange } from './calculateVersionChanges';
import calculateVersionChanges from './calculateVersionChanges';
import NewPostHistoryMagneticWall from './NewPostHistoryMagneticWall';
import * as styles from './NewPostHistoryView.module.scss';
import partitionAndGroupHistories from './partitionAndGroupHistories';
import useRestoreVersion from './useRestoreVersion';
import VersionChanges from './VersionChanges';
import VersionChangesDropdown from './VersionChangesDropdown';

const ANNOTATION_FRAGMENT = gql(`
  fragment BaseNewPostHistoryView_annotation on Annotation {
    id
    ...NewPostHistoryMagneticWall_annotation
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment BaseNewPostHistoryView_thread on CommentThread {
    id
    ...NewPostHistoryMagneticWall_thread
  }
`);

const POST_FRAGMENT = gql(`
  fragment BaseNewPostHistoryView_post on Post {
    id
    title
    publishedAt
    archivedAt
    histories {
      id
      ...partitionAndGroupHistories_history
    }
    ...VersionChangesDropdown_post
    ...ControlBarContributorsButton_post
    ...usePostPolicyCheck_post
  }  
`);

type VersionRange = {
  from: number;
  to: number;
};

interface BaseNewPostHistoryViewProps {
  allVersionChanges?: VersionChange[];
  insertedAt: string;
  versionRange?: VersionRange;
  content?: Delta;
  threads: DocumentType<typeof THREAD_FRAGMENT>[];
  annotations: DocumentType<typeof ANNOTATION_FRAGMENT>[];
  post: DocumentType<typeof POST_FRAGMENT>;
  showAnnotationNewBadge: boolean;
}

const BaseNewPostHistoryView = ({
  allVersionChanges,
  versionRange,
  insertedAt,
  content,
  threads,
  annotations,
  post,
  showAnnotationNewBadge,
}: BaseNewPostHistoryViewProps) => {
  const dispatch = useAppDispatch();

  const { quill } = useQuill();

  const [hoverableAreaContainer, setHoverableAreaContainer] =
    useState<HTMLElement | null>(null);
  const [magneticWallHighlight, setMagneticWallHighlight] =
    useState<MagneticWallHighlight | null>(null);
  const [magneticWallFocus, setMagneticWallFocus] =
    useState<MagneticWallFocus | null>(null);

  useSyncMagneticWallStates({ magneticWallFocus, magneticWallHighlight });
  useHeaderCollapse(quill);
  useListCollapse(quill);

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(
    quill,
    post.id,
    {
      updateLocationHash: true,
    },
  );

  const hasHeadings = useMemo(
    () => content?.ops.some((op) => op.attributes?.header != null),
    [content?.ops],
  );

  const hasThreadsOrAnnotations = useMemo(
    () =>
      hasThreadsOrAnnotationsInOps(
        content?.ops ?? [],
        new Set(threads.map((thread) => thread.id)),
        new Set(annotations.map((annotation) => annotation.id)),
      ),
    [content?.ops, threads, annotations],
  );

  const { accessibleHistories } = partitionAndGroupHistories(post.histories);

  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);
  const lastAccessibleHistory = accessibleHistories.at(0);

  const canRestore =
    canEdit &&
    versionRange &&
    lastAccessibleHistory &&
    versionRange.to !== lastAccessibleHistory.versionTo;

  const restoreVersion = useRestoreVersion();

  return (
    <VersionChanges.Root
      allVersionChanges={allVersionChanges}
      onFocusChange={() => setMagneticWallFocus(null)}
      hideFocusedDiff={magneticWallFocus != null}
    >
      <PageMeta title={`${post.title} - History - Slab`} />
      <PostLayoutProvider
        initialPriorityList={['sidebar', 'magneticWall', 'toc']}
        // Force new layout when the version changes
        key={versionRange && `${versionRange.from}-${versionRange.to}`}
        expandableAreas={{
          sidebar: true,
          toc: hasHeadings,
          magneticWall: Boolean(
            allVersionChanges?.length || hasThreadsOrAnnotations,
          ),
        }}
      >
        <PostLayout.Root
          {...anchorScrollingHandlerProps}
          topbar={
            <VersionChanges.PostLayoutTopbarContainer>
              <VersionChanges.Topbar
                overflowMenu={
                  canRestore && (
                    <MenuItem
                      icon={historyIcon}
                      onClick={() => {
                        dispatch(
                          UIActions.openModal(ConfirmationModal, {
                            title: 'Restore version?',
                            confirmButtonText: 'Restore',
                            message:
                              'This action will update this post’s contents to this version’s contents. This restore will appear as a new version added to this post’s history.',
                            onConfirm: () => {
                              return restoreVersion({
                                postId: post.id,
                                versionFrom: versionRange.from,
                                versionTo: versionRange.to,
                              });
                            },
                          }),
                        );
                      }}
                    >
                      Restore this version
                    </MenuItem>
                  )
                }
              >
                <VersionChangesDropdown
                  post={post}
                  version={versionRange?.to}
                />
              </VersionChanges.Topbar>
            </VersionChanges.PostLayoutTopbarContainer>
          }
        >
          <PostLayout.ControlBar>
            <div className={styles.controlBar}>
              <ControlBarContributorsButton post={post} interactionsDisabled>
                Edited <Timestamp date={insertedAt} {...ignoreInChromatic()} />
              </ControlBarContributorsButton>
            </div>
          </PostLayout.ControlBar>
          <PostLayout.Above />
          <PostLayout.TOC>
            <NewPostHistoryViewTOC postId={post.id} />
          </PostLayout.TOC>
          <PostLayout.Editor>
            <AttributeHandler
              onAttributeClick={(attribute, type) => {
                setMagneticWallFocus({
                  active: true,
                  id: attribute.id,
                  type,
                  hasExpandIntent: false,
                });
              }}
            >
              <EditorContainer ref={setHoverableAreaContainer}>
                <VersionChanges.Post content={content} />
                {quill && hoverableAreaContainer && (
                  <Gutter
                    quill={quill}
                    hoverableAreaContainer={hoverableAreaContainer}
                    config={{
                      listCollapsingEnabled: true,
                    }}
                  />
                )}
              </EditorContainer>
            </AttributeHandler>
          </PostLayout.Editor>
          <PostLayout.MagneticWall>
            <NewPostHistoryMagneticWall
              threads={threads}
              annotations={annotations}
              historyInsertedAt={new Date(insertedAt)}
              setMagneticWallFocus={setMagneticWallFocus}
              magneticWallFocus={magneticWallFocus}
              setMagneticWallHighlight={setMagneticWallHighlight}
              magneticWallHighlight={magneticWallHighlight}
              showAnnotationNewBadge={showAnnotationNewBadge}
              canEdit={canEdit}
            />
          </PostLayout.MagneticWall>
          {post.publishedAt && (
            <PostLayout.Below>
              <ControlBarContributorsButton post={post} interactionsDisabled>
                {post.archivedAt ? (
                  <>
                    Archived <Timestamp date={post.archivedAt} />
                  </>
                ) : (
                  <>
                    Published <Timestamp date={post.publishedAt} />
                  </>
                )}
              </ControlBarContributorsButton>
            </PostLayout.Below>
          )}
        </PostLayout.Root>
      </PostLayoutProvider>
    </VersionChanges.Root>
  );
};

const NEW_POST_HISTORY_VIEW_QUERY = gql(/** @complexity 31 */ `
  query NewPostHistoryView($id: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $id) {
      id
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
        diff
        insertedAt
        annotations {
          id
          ...BaseNewPostHistoryView_annotation
        }
        threads {
          id
          ...BaseNewPostHistoryView_thread
        }
      }
      ...BaseNewPostHistoryView_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const getSelectedVersionRange = (
  histories: DocumentType<
    typeof NEW_POST_HISTORY_VIEW_QUERY
  >['post']['histories'],
  versionRange: VersionRange,
): VersionRange | null => {
  const { accessibleHistories } = partitionAndGroupHistories(histories ?? []);
  const minHistory = accessibleHistories.find(
    (history) => versionRange.from >= history.versionFrom,
  );

  const maxHistory =
    accessibleHistories
      .toReversed()
      .find((history) => versionRange.to <= history.versionTo) ??
    accessibleHistories.at(0);

  if (!maxHistory) {
    return null;
  }

  return {
    from:
      minHistory?.versionFrom != null
        ? minHistory.versionFrom
        : maxHistory.versionFrom,
    to: maxHistory.versionTo,
  };
};

interface NewPostHistoryViewWithHistoryProps {
  postId: string;
  versionRange: VersionRange;
}

const NewPostHistoryViewWithHistory = ({
  postId,
  versionRange,
}: NewPostHistoryViewWithHistoryProps) => {
  const [, setSearchParams] = useSearchParams();

  const { data } = useQuery(NEW_POST_HISTORY_VIEW_QUERY, {
    variables: {
      id: postId,
      versionFrom: versionRange?.from,
      versionTo: versionRange?.to,
    },
  });

  const post = data?.post;

  const threads = useMemo(
    () => post?.history.threads.filter(isCommentOrPublicCommentThread) ?? [],
    [post?.history.threads],
  );

  const selectedVersionRange = getSelectedVersionRange(
    post?.histories ?? [],
    versionRange,
  );

  useEffect(() => {
    if (
      selectedVersionRange?.from != null &&
      selectedVersionRange?.to != null
    ) {
      if (
        versionRange.from === selectedVersionRange.from &&
        versionRange.to === selectedVersionRange.to
      ) {
        return;
      }

      setSearchParams(
        {
          histories: `${selectedVersionRange.from}-${selectedVersionRange.to}`,
        },
        { replace: true },
      );
    }
  }, [
    setSearchParams,
    selectedVersionRange?.from,
    selectedVersionRange?.to,
    versionRange.from,
    versionRange.to,
  ]);

  // We have this in a separate useMemo to avoid recalculating the version changes
  // when history changes but the diff object is the same. This is for example
  // the case when the user unresolves a thread.
  const calculated = useMemo(
    () =>
      post?.history.diff ? calculateVersionChanges(post.history.diff) : null,
    [post?.history.diff],
  );

  const { content, allVersionChanges } = useMemo(() => {
    if (!post?.history.diff || !calculated) {
      return { content: undefined, allVersionChanges: undefined };
    }

    const composed = new Delta(post?.history.snapshot)
      .compose(new Delta(post?.history.diff))
      .compose(calculated.delta ?? new Delta());

    return {
      content: composed,
      allVersionChanges: calculated.versionChanges,
    };
  }, [calculated, post?.history.snapshot, post?.history.diff]);

  if (!post || !selectedVersionRange) {
    return null;
  }

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <BaseNewPostHistoryView
        allVersionChanges={allVersionChanges}
        versionRange={selectedVersionRange}
        insertedAt={post.history.insertedAt}
        content={content}
        threads={threads}
        annotations={post.history.annotations}
        post={post}
        showAnnotationNewBadge
      />
    </AssetTokenProvider>
  );
};

const NEW_POST_HISTORY_VIEW_WITHOUT_VERSION_RANGE_QUERY =
  gql(/** @complexity 31 */ `
  query NewPostHistoryViewWithoutVersionRange($id: ID!) {
    post(id: $id) {
      id
      updatedAt
      content{
        id
        delta
      }
      annotations {
        id
        ...BaseNewPostHistoryView_annotation
      }
      histories {
        id
        ...partitionAndGroupHistories_history
      }
      threads {
        id
        ...BaseNewPostHistoryView_thread
      }
      ...BaseNewPostHistoryView_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface NewPostHistoryViewWithoutVersionRangeProps {
  postId: string;
  redirectToLastHistory: boolean;
}

const NewPostHistoryViewWithoutVersionRange = ({
  postId,
  redirectToLastHistory,
}: NewPostHistoryViewWithoutVersionRangeProps) => {
  const [, setSearchParams] = useSearchParams();
  const { data } = useQuery(NEW_POST_HISTORY_VIEW_WITHOUT_VERSION_RANGE_QUERY, {
    variables: {
      id: postId,
    },
  });

  const post = data?.post;
  const threads = useMemo(
    () => post?.threads.filter(isCommentOrPublicCommentThread) ?? [],
    [post?.threads],
  );

  useLayoutEffect(() => {
    if (!redirectToLastHistory) return;
    const { accessibleHistories } = partitionAndGroupHistories(
      post?.histories ?? [],
    );
    const lastAccessibleHistory = accessibleHistories.at(0);
    if (!lastAccessibleHistory) return;

    const { versionFrom, versionTo } = lastAccessibleHistory;

    setSearchParams(
      {
        histories: `${versionFrom}-${versionTo}`,
      },
      { replace: true },
    );
  }, [post, redirectToLastHistory, setSearchParams]);

  if (!post || redirectToLastHistory) return null;

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <BaseNewPostHistoryView
        insertedAt={post.updatedAt}
        content={new Delta(post.content.delta)}
        threads={threads}
        annotations={post.annotations}
        post={post}
        showAnnotationNewBadge={false}
      />
    </AssetTokenProvider>
  );
};

interface NewPostHistoryViewProps {
  postId: string;
}

const NewPostHistoryView = ({ postId }: NewPostHistoryViewProps) => {
  const [searchParams] = useSearchParams();

  const historiesParam = searchParams.get('histories');
  const [, from, to] = historiesParam?.match(/(\d+)-(\d+)/) ?? [];

  return (
    <Page
      topbar={
        <InternalTopbar
          postActions={
            <Button
              variant="secondary"
              to={postPath({ id: postId, title: '' })}
            >
              Done
            </Button>
          }
        />
      }
    >
      <QuillContext>
        {from && to ? (
          <NewPostHistoryViewWithHistory
            postId={postId}
            versionRange={{
              from: parseInt(from, 10),
              to: parseInt(to, 10),
            }}
          />
        ) : (
          <NewPostHistoryViewWithoutVersionRange
            postId={postId}
            redirectToLastHistory={historiesParam === 'last'}
          />
        )}
      </QuillContext>
    </Page>
  );
};

export default NewPostHistoryView;
